import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut, CalendarDays, CreditCard, Mail, MessageSquare, FileText, User, RefreshCw, Check,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const TABS = [
  { id: "reservations", label: "Reservas", icon: CalendarDays },
  { id: "payments", label: "Pagos", icon: CreditCard },
  { id: "email", label: "Email (SMTP)", icon: Mail },
  { id: "wpp", label: "WhatsApp", icon: MessageSquare },
  { id: "templates", label: "Plantillas", icon: FileText },
  { id: "account", label: "Cuenta", icon: User },
];

/* ---------- Helpers ---------- */
const Input = ({ label, ...props }) => (
  <label className="block">
    <span className="overline block mb-2">{label}</span>
    <input {...props} className={`w-full bg-transparent border-b border-white/20 text-[#F5F3F0] py-2.5 focus:outline-none focus:border-ember ${props.className || ""}`} />
  </label>
);
const TextArea = ({ label, ...props }) => (
  <label className="block">
    <span className="overline block mb-2">{label}</span>
    <textarea {...props} className={`w-full bg-transparent border border-white/20 text-[#F5F3F0] p-3 focus:outline-none focus:border-ember ${props.className || ""}`} />
  </label>
);
const Toggle = ({ label, value, onChange, testId }) => (
  <button type="button" onClick={() => onChange(!value)} data-testid={testId}
    className={`flex items-center justify-between w-full border px-4 py-3 transition-colors ${value ? "border-ember bg-ember/10" : "border-white/20"}`}>
    <span className="text-sm text-[#F5F3F0]">{label}</span>
    <span className={`h-5 w-10 relative border ${value ? "border-ember" : "border-white/20"}`}>
      <span className={`absolute top-0 h-[18px] w-[18px] bg-ember transition-all ${value ? "right-0" : "left-0 bg-white/40"}`} />
    </span>
  </button>
);

/* ---------- Reservations Tab ---------- */
function ReservationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reservations");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/reservations/${id}/status?status=${status}`);
      await load();
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-reservations-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-display text-3xl text-[#F5F3F0]">Reservas</h2>
          <p className="text-[#A3A3A3] text-sm mt-1">{items.length} en total</p>
        </div>
        <button onClick={load} className="btn-ghost" data-testid="reload-reservations-btn">
          <RefreshCw size={14} /> Refrescar
        </button>
      </div>

      {loading ? (
        <p className="text-[#A3A3A3]">Cargando...</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-12 text-center">
          <p className="text-[#A3A3A3]">Aún no hay reservas. Las que lleguen aparecerán acá.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3] border-b border-white/10">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Pax</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Pago</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-ember/5" data-testid={`reservation-row-${r.id}`}>
                  <td className="px-4 py-4">
                    <p className="text-[#F5F3F0]">{r.date}</p>
                    <p className="text-[#A3A3A3] text-xs">{r.time_slot}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-[#F5F3F0]">{r.name}</p>
                    <p className="text-[#A3A3A3] text-xs">{r.email}</p>
                    {r.phone && <p className="text-[#A3A3A3] text-xs">{r.phone}</p>}
                  </td>
                  <td className="px-4 py-4 text-[#F5F3F0]">{r.guests}</td>
                  <td className="px-4 py-4 text-ember">USD {r.amount_usd.toFixed(2)}</td>
                  <td className="px-4 py-4 uppercase text-[10px] tracking-[0.2em] text-[#A3A3A3]">{r.payment_provider}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.18em] border ${
                      r.payment_status === "paid" ? "border-ember text-ember" :
                      r.payment_status === "pending" ? "border-amber-400/50 text-amber-400" :
                      "border-red-500/50 text-red-400"
                    }`}>{r.payment_status}</span>
                  </td>
                  <td className="px-4 py-4">
                    <select defaultValue={r.payment_status}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      data-testid={`status-select-${r.id}`}
                      className="bg-transparent border border-white/10 text-[#F5F3F0] text-xs px-2 py-1.5 focus:outline-none focus:border-ember">
                      <option value="pending">pending</option>
                      <option value="paid">paid</option>
                      <option value="failed">failed</option>
                      <option value="cancelled">cancelled</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Settings wrapper hook ---------- */
function useSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data.settings || {});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try {
      const { data } = await api.put("/admin/settings", { settings: patch });
      setSettings(data.settings || {});
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const set = (key, value) => setSettings((s) => ({ ...s, [key]: value }));
  return { settings, loading, save, set };
}

/* ---------- Payments Tab ---------- */
function PaymentsTab() {
  const { settings, loading, save, set } = useSettings();
  if (loading) return <p className="text-[#A3A3A3]">Cargando...</p>;

  const saveAll = () => save({
    price_per_guest: settings.price_per_guest || "65",
    stripe_enabled: settings.stripe_enabled || "false",
    stripe_public_key: settings.stripe_public_key || "",
    stripe_secret_key: settings.stripe_secret_key || "",
    mercadopago_enabled: settings.mercadopago_enabled || "false",
    mercadopago_public_key: settings.mercadopago_public_key || "",
    mercadopago_access_token: settings.mercadopago_access_token || "",
  });

  return (
    <div className="space-y-10" data-testid="admin-payments-tab">
      <div>
        <h2 className="font-display text-3xl text-[#F5F3F0]">Pagos</h2>
        <p className="text-[#A3A3A3] text-sm mt-1">Configurá los proveedores. Los secretos se muestran enmascarados.</p>
      </div>

      <section className="border border-white/10 p-6 space-y-6">
        <h3 className="font-display text-xl text-ember">Precio</h3>
        <Input label="Precio por persona (USD)" value={settings.price_per_guest || ""}
          onChange={(e) => set("price_per_guest", e.target.value)} type="number" data-testid="price-input" />
      </section>

      <section className="border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl text-ember">Stripe</h3>
          <Toggle label="Habilitar Stripe" value={settings.stripe_enabled === "true"}
            onChange={(v) => set("stripe_enabled", v ? "true" : "false")} testId="stripe-enabled-toggle" />
        </div>
        <Input label="Public key (pk_...)" value={settings.stripe_public_key || ""}
          onChange={(e) => set("stripe_public_key", e.target.value)} data-testid="stripe-public-input" />
        <Input label="Secret key (sk_...)" value={settings.stripe_secret_key || ""}
          onChange={(e) => set("stripe_secret_key", e.target.value)} data-testid="stripe-secret-input"
          placeholder="Se mostrará enmascarado después de guardar" />
      </section>

      <section className="border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl text-ember">MercadoPago</h3>
          <Toggle label="Habilitar MercadoPago" value={settings.mercadopago_enabled === "true"}
            onChange={(v) => set("mercadopago_enabled", v ? "true" : "false")} testId="mp-enabled-toggle" />
        </div>
        <Input label="Public key" value={settings.mercadopago_public_key || ""}
          onChange={(e) => set("mercadopago_public_key", e.target.value)} data-testid="mp-public-input" />
        <Input label="Access token" value={settings.mercadopago_access_token || ""}
          onChange={(e) => set("mercadopago_access_token", e.target.value)} data-testid="mp-token-input" />
      </section>

      <button className="btn-primary" onClick={saveAll} data-testid="save-payments-btn">Guardar</button>
    </div>
  );
}

/* ---------- Email (SMTP) Tab ---------- */
function EmailTab() {
  const { settings, loading, save, set } = useSettings();
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  if (loading) return <p className="text-[#A3A3A3]">Cargando...</p>;

  const saveAll = () => save({
    smtp_enabled: settings.smtp_enabled || "false",
    smtp_host: settings.smtp_host || "",
    smtp_port: settings.smtp_port || "587",
    smtp_user: settings.smtp_user || "",
    smtp_password: settings.smtp_password || "",
    smtp_from: settings.smtp_from || "",
    smtp_admin_recipient: settings.smtp_admin_recipient || "",
  });

  const sendTest = async () => {
    if (!testTo) { toast.error("Poné un destinatario"); return; }
    setTesting(true);
    try {
      await api.post("/admin/test-email", { to: testTo });
      toast.success("Email de prueba enviado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-10" data-testid="admin-email-tab">
      <div>
        <h2 className="font-display text-3xl text-[#F5F3F0]">Email (SMTP)</h2>
        <p className="text-[#A3A3A3] text-sm mt-1">Config de tu servidor SMTP para confirmaciones y avisos.</p>
      </div>

      <section className="border border-white/10 p-6 space-y-6">
        <Toggle label="Habilitar envío de emails" value={settings.smtp_enabled === "true"}
          onChange={(v) => set("smtp_enabled", v ? "true" : "false")} testId="smtp-enabled-toggle" />
        <div className="grid md:grid-cols-2 gap-6">
          <Input label="Host" value={settings.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} data-testid="smtp-host-input" placeholder="smtp.gmail.com" />
          <Input label="Puerto" value={settings.smtp_port || ""} onChange={(e) => set("smtp_port", e.target.value)} data-testid="smtp-port-input" placeholder="587" />
          <Input label="Usuario" value={settings.smtp_user || ""} onChange={(e) => set("smtp_user", e.target.value)} data-testid="smtp-user-input" />
          <Input label="Contraseña" type="password" value={settings.smtp_password || ""} onChange={(e) => set("smtp_password", e.target.value)} data-testid="smtp-password-input" />
          <Input label="Remitente (From)" value={settings.smtp_from || ""} onChange={(e) => set("smtp_from", e.target.value)} data-testid="smtp-from-input" />
          <Input label="Email admin (recibe avisos)" value={settings.smtp_admin_recipient || ""} onChange={(e) => set("smtp_admin_recipient", e.target.value)} data-testid="smtp-admin-input" />
        </div>
      </section>

      <div className="flex flex-wrap gap-4">
        <button className="btn-primary" onClick={saveAll} data-testid="save-email-btn">Guardar</button>
      </div>

      <section className="border border-white/10 p-6 space-y-4">
        <h3 className="font-display text-xl text-ember">Probar envío</h3>
        <div className="flex flex-col md:flex-row gap-4">
          <input placeholder="destinatario@ejemplo.com" value={testTo} onChange={(e) => setTestTo(e.target.value)}
            className="flex-1 bg-transparent border-b border-white/20 text-[#F5F3F0] py-2.5 focus:outline-none focus:border-ember"
            data-testid="test-email-to-input" />
          <button className="btn-ghost" onClick={sendTest} disabled={testing} data-testid="test-email-send-btn">
            <Mail size={14} /> {testing ? "Enviando..." : "Enviar prueba"}
          </button>
        </div>
      </section>
    </div>
  );
}

/* ---------- WhatsApp (WPPConnect) Tab ---------- */
function WppTab() {
  const { settings, loading, save, set } = useSettings();
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  if (loading) return <p className="text-[#A3A3A3]">Cargando...</p>;

  const saveAll = () => save({
    wpp_enabled: settings.wpp_enabled || "false",
    wpp_base_url: settings.wpp_base_url || "",
    wpp_session: settings.wpp_session || "",
    wpp_token: settings.wpp_token || "",
    wpp_target_number: settings.wpp_target_number || "",
  });

  const sendTest = async () => {
    if (!testTo) { toast.error("Poné un número destino"); return; }
    setTesting(true);
    try {
      await api.post("/admin/test-wpp", { to: testTo });
      toast.success("Mensaje enviado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-10" data-testid="admin-wpp-tab">
      <div>
        <h2 className="font-display text-3xl text-[#F5F3F0]">WhatsApp · WPPConnect</h2>
        <p className="text-[#A3A3A3] text-sm mt-1">Config de tu instancia WPPConnect para los avisos de reservas.</p>
      </div>

      <section className="border border-white/10 p-6 space-y-6">
        <Toggle label="Habilitar envío WhatsApp" value={settings.wpp_enabled === "true"}
          onChange={(v) => set("wpp_enabled", v ? "true" : "false")} testId="wpp-enabled-toggle" />
        <div className="grid md:grid-cols-2 gap-6">
          <Input label="Base URL" value={settings.wpp_base_url || ""} onChange={(e) => set("wpp_base_url", e.target.value)} data-testid="wpp-url-input" placeholder="https://wpp.example.com" />
          <Input label="Session" value={settings.wpp_session || ""} onChange={(e) => set("wpp_session", e.target.value)} data-testid="wpp-session-input" />
          <Input label="Token (Bearer)" type="password" value={settings.wpp_token || ""} onChange={(e) => set("wpp_token", e.target.value)} data-testid="wpp-token-input" />
          <Input label="Número destino admin" value={settings.wpp_target_number || ""} onChange={(e) => set("wpp_target_number", e.target.value)} data-testid="wpp-target-input" placeholder="549115555..." />
        </div>
      </section>

      <div>
        <button className="btn-primary" onClick={saveAll} data-testid="save-wpp-btn">Guardar</button>
      </div>

      <section className="border border-white/10 p-6 space-y-4">
        <h3 className="font-display text-xl text-ember">Probar envío</h3>
        <div className="flex flex-col md:flex-row gap-4">
          <input placeholder="549115555..." value={testTo} onChange={(e) => setTestTo(e.target.value)}
            className="flex-1 bg-transparent border-b border-white/20 text-[#F5F3F0] py-2.5 focus:outline-none focus:border-ember"
            data-testid="test-wpp-to-input" />
          <button className="btn-ghost" onClick={sendTest} disabled={testing} data-testid="test-wpp-send-btn">
            <MessageSquare size={14} /> {testing ? "Enviando..." : "Enviar prueba"}
          </button>
        </div>
      </section>
    </div>
  );
}

/* ---------- Templates Tab ---------- */
function TemplatesTab() {
  const { settings, loading, save, set } = useSettings();
  if (loading) return <p className="text-[#A3A3A3]">Cargando...</p>;

  const saveAll = () => save({
    tpl_email_confirm_subject_es: settings.tpl_email_confirm_subject_es || "",
    tpl_email_confirm_body_es: settings.tpl_email_confirm_body_es || "",
    tpl_email_confirm_subject_en: settings.tpl_email_confirm_subject_en || "",
    tpl_email_confirm_body_en: settings.tpl_email_confirm_body_en || "",
    tpl_email_admin_subject: settings.tpl_email_admin_subject || "",
    tpl_email_admin_body: settings.tpl_email_admin_body || "",
    tpl_wpp_admin_body: settings.tpl_wpp_admin_body || "",
  });

  const Help = () => (
    <p className="text-xs text-[#A3A3A3] leading-relaxed">
      Variables disponibles: <code className="text-ember">{"{name}"}</code>, <code className="text-ember">{"{email}"}</code>,
      <code className="text-ember">{" {phone}"}</code>, <code className="text-ember">{"{date}"}</code>,
      <code className="text-ember">{" {time_slot}"}</code>, <code className="text-ember">{"{guests}"}</code>,
      <code className="text-ember">{" {amount}"}</code>, <code className="text-ember">{"{payment_provider}"}</code>,
      <code className="text-ember">{" {payment_status}"}</code>, <code className="text-ember">{"{notes}"}</code>,
      <code className="text-ember">{" {reservation_id}"}</code>.
    </p>
  );

  return (
    <div className="space-y-10" data-testid="admin-templates-tab">
      <div>
        <h2 className="font-display text-3xl text-[#F5F3F0]">Plantillas</h2>
        <Help />
      </div>

      <section className="border border-white/10 p-6 space-y-6">
        <h3 className="font-display text-xl text-ember">Email confirmación — Español</h3>
        <Input label="Asunto" value={settings.tpl_email_confirm_subject_es || ""}
          onChange={(e) => set("tpl_email_confirm_subject_es", e.target.value)} data-testid="tpl-es-subject" />
        <TextArea label="Cuerpo" rows={8} value={settings.tpl_email_confirm_body_es || ""}
          onChange={(e) => set("tpl_email_confirm_body_es", e.target.value)} data-testid="tpl-es-body" />
      </section>

      <section className="border border-white/10 p-6 space-y-6">
        <h3 className="font-display text-xl text-ember">Email confirmation — English</h3>
        <Input label="Subject" value={settings.tpl_email_confirm_subject_en || ""}
          onChange={(e) => set("tpl_email_confirm_subject_en", e.target.value)} data-testid="tpl-en-subject" />
        <TextArea label="Body" rows={8} value={settings.tpl_email_confirm_body_en || ""}
          onChange={(e) => set("tpl_email_confirm_body_en", e.target.value)} data-testid="tpl-en-body" />
      </section>

      <section className="border border-white/10 p-6 space-y-6">
        <h3 className="font-display text-xl text-ember">Email aviso admin</h3>
        <Input label="Asunto" value={settings.tpl_email_admin_subject || ""}
          onChange={(e) => set("tpl_email_admin_subject", e.target.value)} data-testid="tpl-admin-subject" />
        <TextArea label="Cuerpo" rows={8} value={settings.tpl_email_admin_body || ""}
          onChange={(e) => set("tpl_email_admin_body", e.target.value)} data-testid="tpl-admin-body" />
      </section>

      <section className="border border-white/10 p-6 space-y-6">
        <h3 className="font-display text-xl text-ember">WhatsApp aviso admin</h3>
        <TextArea label="Mensaje" rows={8} value={settings.tpl_wpp_admin_body || ""}
          onChange={(e) => set("tpl_wpp_admin_body", e.target.value)} data-testid="tpl-wpp-body" />
      </section>

      <button className="btn-primary" onClick={saveAll} data-testid="save-templates-btn">Guardar plantillas</button>
    </div>
  );
}

/* ---------- Account Tab ---------- */
function AccountTab() {
  const { user, refresh } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");

  const changeEmail = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-email", { new_email: newEmail, current_password: emailPwd });
      await refresh();
      toast.success("Email actualizado");
      setNewEmail(""); setEmailPwd("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd });
      toast.success("Contraseña actualizada");
      setCurPwd(""); setNewPwd("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-10 max-w-2xl" data-testid="admin-account-tab">
      <div>
        <h2 className="font-display text-3xl text-[#F5F3F0]">Cuenta</h2>
        <p className="text-[#A3A3A3] text-sm mt-1">Email actual: <span className="text-ember">{user?.email}</span></p>
      </div>

      <form onSubmit={changeEmail} className="border border-white/10 p-6 space-y-6">
        <h3 className="font-display text-xl text-ember">Cambiar email</h3>
        <Input label="Nuevo email" type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-email-input" />
        <Input label="Contraseña actual" type="password" required value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)} data-testid="email-cur-pwd-input" />
        <button className="btn-primary" type="submit" data-testid="change-email-btn"><Check size={14} /> Actualizar email</button>
      </form>

      <form onSubmit={changePwd} className="border border-white/10 p-6 space-y-6">
        <h3 className="font-display text-xl text-ember">Cambiar contraseña</h3>
        <Input label="Contraseña actual" type="password" required value={curPwd} onChange={(e) => setCurPwd(e.target.value)} data-testid="cur-pwd-input" />
        <Input label="Nueva contraseña" type="password" minLength={6} required value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="new-pwd-input" />
        <button className="btn-primary" type="submit" data-testid="change-pwd-btn"><Check size={14} /> Actualizar contraseña</button>
      </form>
    </div>
  );
}

/* ---------- Dashboard Shell ---------- */
export default function AdminDashboard() {
  const [tab, setTab] = useState("reservations");
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-charred/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-brand">
            <span className="relative inline-flex h-6 w-6 items-center justify-center border border-ember/60">
              <span className="absolute h-[2px] w-3.5 bg-ember rotate-45"></span>
              <span className="absolute h-[2px] w-3.5 bg-ember -rotate-45"></span>
            </span>
            <span className="font-display text-sm tracking-[0.28em] uppercase">
              Asado <span className="text-ember">Xperience</span> · Admin
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <span className="text-xs text-[#A3A3A3] hidden md:block">{user?.email}</span>
            <button onClick={doLogout} className="btn-ghost" data-testid="admin-logout-btn">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 grid md:grid-cols-12 gap-10">
        <aside className="md:col-span-3 lg:col-span-2">
          <nav className="flex md:flex-col gap-2 overflow-x-auto" data-testid="admin-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`flex items-center gap-3 px-4 py-3 border text-left transition-colors
                  ${active ? "border-ember text-ember bg-ember/10" : "border-transparent text-[#A3A3A3] hover:text-[#F5F3F0]"}`}>
                  <Icon size={14} /> <span className="text-xs uppercase tracking-[0.2em]">{t.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>
        <main className="md:col-span-9 lg:col-span-10 min-h-[60vh]">
          {tab === "reservations" && <ReservationsTab />}
          {tab === "payments" && <PaymentsTab />}
          {tab === "email" && <EmailTab />}
          {tab === "wpp" && <WppTab />}
          {tab === "templates" && <TemplatesTab />}
          {tab === "account" && <AccountTab />}
        </main>
      </div>
    </div>
  );
}
